import { useMemo } from "react"

import { HighlightBoxType } from "@/components/common/PdfViewer/PdfIntelligenceViewer"
import { PDF_ANSWER_BOX_DEFAULT_FONT_SIZE } from "@/constants/UiConstants"
import { Questionnaire } from "@/types/questionnaire/models"

import { fitAnswerText } from "@/utils/questionnaire/fitAnswerText"
import { safeParseStringArray } from "@/utils/questionnaire/optionText"
import { createOptionBoxId } from "../utils/boxIdUtils"
import { getOptionsPlacements } from "../utils/optionsPlacementUtils"
import { normalizeOptionText } from "../utils/placementUtils"

const isFreeTextQuestion = (type?: string) => type === "free-text"

const isChoiceQuestion = (type?: string) =>
  type === "boolean" || type === "multiple-choice" || type === "multiple-select"

const isOptionSelected = (
  questionType: string,
  response: string,
  optionText: string,
): boolean => {
  if (questionType === "multiple-select") {
    return safeParseStringArray(response).some(
      (v) => normalizeOptionText(v) === normalizeOptionText(optionText),
    )
  }
  return normalizeOptionText(response) === normalizeOptionText(optionText)
}

export const useHighlightBoxes = (
  questionnaireData: Questionnaire | undefined,
) => {
  // Derive from the questionnaire's stored extraction stamp (serialized by the backend),
  // NOT the viewer's USE_EXTEND feature flag — so the interactive overlay matches the
  // downloaded PDF for any viewer, exactly like the backend renderer now does.
  const useExtend = questionnaireData?.usesExtend ?? false

  return useMemo(() => {
    const boxes: HighlightBoxType[] = []

    if (!questionnaireData) return boxes

    // Only apply shrink-to-fit for Extend-extracted questionnaires (per the stored
    // `usesExtend` stamp, above); otherwise preserve the existing behaviour (raw font size,
    // unmodified text) so non-Extend questionnaires are unaffected.
    const fit = (
      text: string,
      width: number,
      height: number,
      fontSize: number,
    ) =>
      useExtend
        ? fitAnswerText(text, width, height, fontSize)
        : { fontSize, text }

    questionnaireData.sections.forEach((section) => {
      section.responsePairs.forEach((question) => {
        const pdfPlacement = question?.pdfPlacement
        const responseText = question?.response ?? ""
        const extractedFontSize = pdfPlacement?.extraction?.fontSize
        const defaultFontSize = PDF_ANSWER_BOX_DEFAULT_FONT_SIZE

        if (isFreeTextQuestion(question.type)) {
          const userPlacement = pdfPlacement?.userPlacement
          const extractedBox = pdfPlacement?.extraction?.answerBboxHint

          const fontSize = extractedFontSize || defaultFontSize

          // A free-text cell answered with a check-mark glyph must show the same 'X' the
          // download renders (helv has no ✓, so the server draws 'X'), matching the backend's
          // _sanitize_for_helv which maps these for every flow. fitAnswerText.sanitizeForHelv
          // also maps them (so measurement/other callers stay in sync), but that only runs for
          // Extend questionnaires (the non-Extend fit() path skips fitAnswerText) — so map here
          // unconditionally to keep preview and download consistent in the legacy flow too.
          const displayText = responseText.replace(/[✓✔☑✅]/g, "X")

          if (userPlacement) {
            // Shrink-to-fit exactly like the server-rendered PDF (see fitAnswerText).
            const fitted = fit(
              displayText,
              userPlacement.width,
              userPlacement.height,
              userPlacement.fontSize || fontSize,
            )
            boxes.push({
              id: question.id,
              pageNumber: userPlacement.page,
              x: userPlacement.x,
              y: userPlacement.y,
              width: userPlacement.width,
              height: userPlacement.height,
              title: fitted.text,
              fontSize: fitted.fontSize,
            })
          } else if (extractedBox) {
            const fitted = fit(
              displayText,
              extractedBox.w,
              extractedBox.h,
              fontSize,
            )
            boxes.push({
              id: question.id,
              pageNumber: extractedBox.page,
              x: extractedBox.x,
              y: extractedBox.y,
              width: extractedBox.w,
              height: extractedBox.h,
              title: fitted.text,
              fontSize: fitted.fontSize,
            })
          }
        } else if (isChoiceQuestion(question.type)) {
          const choiceFontSize =
            pdfPlacement?.userPlacement?.fontSize ||
            extractedFontSize ||
            defaultFontSize

          const optionsPlacements = getOptionsPlacements(
            question,
            pdfPlacement?.userPlacement,
          )

          const isManualBoolean =
            question.type === "boolean" && question.creationSource === "manual"

          if (optionsPlacements.length > 0) {
            optionsPlacements.forEach((option, i) => {
              const selected = isOptionSelected(
                question.type || "boolean",
                question.response || "",
                option.text,
              )

              // An auto-detected checkbox mark is the glyph "X" drawn INTO the option box.
              // It must be centred on both axes and sized to the box (matching the backend's
              // centred, box-capped X); using the text font size + horizontal-only centring
              // makes it sit top-aligned and oversized, so the preview looks skewed until the
              // finalised (backend-rendered) PDF snaps it back. Manual booleans render their
              // option label as ordinary text, so they keep the text styling.
              const isCheckmark = !isManualBoolean && selected
              // Manual booleans draw their label as text, so shrink it to the box like the PDF.
              // The auto-detected "X" mark is already sized to the box below — do not refit it.
              const manualFit = isManualBoolean
                ? fit(option.text, option.width, option.height, choiceFontSize)
                : null
              boxes.push({
                id: createOptionBoxId(question.id, i),
                pageNumber: option.page,
                x: option.x,
                y: option.y,
                width: option.width,
                height: option.height,
                title: manualFit ? manualFit.text : selected ? "X" : "",
                fontSize: isCheckmark
                  ? Math.min(option.width, option.height, 12)
                  : manualFit
                    ? manualFit.fontSize
                    : choiceFontSize,
                className: isCheckmark
                  ? "flex items-center justify-center leading-none"
                  : "text-center",
              })
            })
          } else {
            // Fallback: no option bboxes available — place answer text next
            // to the question bbox so the response is still visible on the PDF.
            const userPlacement = pdfPlacement?.userPlacement
            const questionBbox = pdfPlacement?.extraction?.questionBbox
            const answerBbox = pdfPlacement?.extraction?.answerBboxHint
            const fallbackBox = answerBbox || questionBbox

            if (userPlacement) {
              const fitted = fit(
                responseText,
                userPlacement.width,
                userPlacement.height,
                userPlacement.fontSize || choiceFontSize,
              )
              boxes.push({
                id: question.id,
                pageNumber: userPlacement.page,
                x: userPlacement.x,
                y: userPlacement.y,
                width: userPlacement.width,
                height: userPlacement.height,
                title: fitted.text,
                fontSize: fitted.fontSize,
              })
            } else if (fallbackBox && responseText) {
              const boxWidth = answerBbox ? fallbackBox.w : 120
              const fitted = fit(
                responseText,
                boxWidth,
                fallbackBox.h,
                choiceFontSize,
              )
              boxes.push({
                id: question.id,
                pageNumber: fallbackBox.page,
                x: answerBbox
                  ? fallbackBox.x
                  : fallbackBox.x + fallbackBox.w + 2,
                y: fallbackBox.y,
                width: boxWidth,
                height: fallbackBox.h,
                title: fitted.text,
                fontSize: fitted.fontSize,
              })
            }
          }
        }
      })
    })

    return boxes
  }, [questionnaireData, useExtend])
}
